import { Injectable } from '@angular/core';
import {RequestService} from "../request/request.service";
import {AuditHistoryGroupAndFilterModel} from "./audit-history-group-and-filter.model";
import {KnockoutFieldModel} from "../../components/knockout-field/knockout-field.model";
import {AuditHistoryFieldModel} from "./audit-history-field.model";
import {AuditHistoryModel} from "./audit-history.model";
import {AuditHistoryRelationshipModel} from "./audit-history-relationship.model";
import {FeatureFlagsService} from "../feature-flag/feature-flags.service";
import {LegacyInitializer} from "../initialization/legacy.initializer";
import {UserService} from "../user/user.service";
import {interval, pipe, Subject} from "rxjs";
import {debounce, delay, map, sampleTime, switchMap, switchMapTo, tap, throttle} from "rxjs/operators";
import {intervalBackoff} from "backoff-rxjs";
import {Logger} from '../logging/logger';

interface RequestAuditHistory {
  tableName: string,
  id: string;
  fieldName: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuditHistoryService {

  log = new Logger("AuditHistoryService");

  AUDIT_FIELDS = [
    "AuditedOn",
    "Field",
    "FieldType",
    "Label",
    "NewDisplayValue",
    "NewValue",
    "OldDisplayValue",
    "OldValue",
    "Operation",
    "ReferenceTable",
    "RelatedID",
    "RelatedTable",
    "VersionID"
  ];

  AUDIT_RELATIONSHIPS = [
    "AuditedBy",
    "ImpersonatedBy",
    "RelatedRecord"
  ];

  SLA_FIELDS = ['SLAStatus', 'SLANextDue', 'SLANextDueRecord', 'SLANextThresholdDue', 'SLANextThresholdRecord'];

  WORKFLOW_FIELDS = ['WorkflowStatus', 'PreviousWorkflowStatus', 'PreviousTransition'];

  requestAuditHistoryBackoff = new Subject<RequestAuditHistory>();

  constructor(private requestService: RequestService,
              private featureFlagsService: FeatureFlagsService,
              private legacyInitializer: LegacyInitializer,
              private userService: UserService) { }

  init() {

    (window as any).NG_AUDIT_HISTORY_SERVICE = this;

    const self = this;

    const messageBus = (window as any).KO_MESSAGE_BUS_BRIDGE;

    // Form load and unload events
    messageBus.subscribe(messageBus.CHANNELS.FORM_API_EVENT,
      "#",
      async (data: any, envelope: any) => {

        const debug = self.featureFlagsService.isOn("audit-history-journal-debugging-display");

        if (debug) {
          this.log.setLogLevel("debug");

          if (data?._eventType !== 'onChange') {
            console.log("AuditHistory: event " + messageBus.CHANNELS.FORM_API_EVENT + " envelope:", envelope);
            console.log(data);
          }
        }

        // We don't need the audit records on form load, as the filter control will request them if necessary when it
        // has loaded the toggle state.
        let auditRecordsRequired = false;
        let id = null;
        let tableName = null;
        let journalFieldNames = [];

        if (envelope.topic.startsWith("onLoad") && data._table && data.current) {
          this.log.debug("onLoad");

          // Load of a DetailsViewModel
          const current = data.current;
          id = current._data._mappedData.id();
          tableName = current._data._tableClass;

          // Determine if the record has any journal fields that require audit data
          const fields = current._data._tableDefinition._data._fields;

          // There is usually only one journal field on a form that cares about audit history [WorkJournal]
          const journalEntries = Object.entries(fields).filter((entry: [string, any]) => {
            const journalField = entry[1];
            return journalField._data._fieldType === "JOURNAL" && journalField._data._fieldName === "WorkJournal";
          });

          if (journalEntries.length > 0) {

            journalFieldNames = journalEntries.map((entry: [string, any]) => {
              return entry[1]._data._fieldName;
            });

          }
        } else if (envelope.topic.startsWith("requestAuditHistory")) {
          this.log.debug("requestAuditHistory");

          // Request audit history again after an updateJournal, or toggle state change
          auditRecordsRequired = true;
          id = data.id;
          tableName = data.tableName;
          journalFieldNames = [data.fieldName];
        }

        if (auditRecordsRequired && id && journalFieldNames && journalFieldNames.length > 0) {
            this.log.debug("requestAuditHistory: getGroupedAuditRecords: " + id + ":" + journalFieldNames);

            const auditHistory = await self.getGroupedAuditRecords(tableName, id, journalFieldNames);

            journalFieldNames.forEach((fieldName) => {

              if (debug) {
                console.log(`AuditHistory: publish ${id} ${fieldName}`);
              }

              // We publish each group as a separate message by a single person as that is what activityLog expects.
              auditHistory.groups.forEach(async (group) => {

                let template = "audit-history-field-changes-markup";
                if (!!group.records.find((record) => {
                  return this.WORKFLOW_FIELDS.includes(record.Field.displayValue);
                })) {
                  template = "audit-history-workflow-changes-markup";
                }
                if (!!group.records.find((record) => {
                  return this.SLA_FIELDS.includes(record.Field.displayValue);
                })) {
                  template = "audit-history-sla-changes-markup";
                }
                if (group.Operation.displayValue === 'Create') {
                  template = "audit-history-create-markup";
                }

                const userInitials = await this.getUserInitials(group.AuditedBy);

                if (debug) {
                  console.log(`AuditHistory: publish group ${group.VersionID.displayValue} ${id} ${fieldName} ${template}`);
                }
                messageBus.publish(messageBus.CHANNELS.ACTIVITY_LOG_EVENT,
                  `ADD.${id}.${fieldName}`,
                  {
                    data: group,
                    userId: group.AuditedBy?.id || 'Unknown id',
                    userName: group.AuditedBy?.displayValue || 'Unknown user',
                    userInitials: userInitials,
                    tags: [],
                    template: template,
                    persist: false,
                    allowClear: true,
                    id: group.VersionID.displayValue,
                    createdOn: group.AuditedOn.value,
                  });
              });

            });
          }
    });

    this.requestAuditHistoryBackoff.pipe(
      throttle(() => intervalBackoff({
        initialInterval: 100,
        maxInterval: 10000,
        backoffDelay: (iteration, initialInterval) => Math.pow(2, iteration) * initialInterval
      })),
    ).subscribe((next) => this.requestAuditHistoryInvoke(next));
  }

  async getAuditRecords(tableName: string, recordId: string, journalFields: string[]): Promise<AuditHistoryModel[]> {
    // Only platform_agent is allowed to see AuditHistory records
    if (this.userService.hasRole("platform_agent")) {

      const response = await (window as any).WS.controller("servicely.core.AuditHistory",  { method: "getAuditHistory", data: { table: tableName, id: recordId, journalFields: journalFields }});

      return this.populateDisplayFields(this.populateNullFields(response.data || []));
    } else {
      return [];
    }
  }

  populateDisplayFields(records: AuditHistoryModel[]): AuditHistoryModel[] {
    if (records) {
      records.forEach((record) => {
        record.NewDisplayValue = this.merge(record.NewDisplayValue, record.NewValue);
        record.OldDisplayValue = this.merge(record.OldDisplayValue, record.OldValue);
      });
    }

    return records;
  }

  populateNullFields(records: AuditHistoryModel[]): AuditHistoryModel[] {
    if (records) {
      records.forEach((record) => {
        this.AUDIT_FIELDS.forEach((name) => {
          if (!record[name]) {
            record[name] = {value: "", displayValue: ""};
          }
        });
      });
    }

    return records;
  }

  async getGroupedAuditRecords(tableName: string, recordId: string, journalFields: string[], filter: (AuditHistoryModel) => boolean = (_) => true): Promise<AuditHistoryGroupAndFilterModel> {
    const records = await this.getAuditRecords(tableName, recordId, journalFields);

    const filteredRecords = records.filter(filter);

    const groups = filteredRecords.reduce((acc, curr) => {
      if (!acc[curr.VersionID.displayValue]) {
        acc[curr.VersionID.displayValue] = {
          AuditedBy: curr.AuditedBy,
          ImpersonatedBy: curr.ImpersonatedBy,
          AuditedOn: curr.AuditedOn,
          Operation: curr.Operation,
          VersionID: curr.VersionID,
          records: []
        };
      }
      acc[curr.VersionID.displayValue].records.push(curr);
      return acc;
    }, {});

    // Place the groups into an array, in numerical order
    const groupedRecords = Object.keys(groups).sort((a, b) => Number(b) - Number(a)).map((g) => {
      return groups[g]
    });

    const header = filteredRecords.length > 0 ? filteredRecords[0]: null;

    return {
      groups: groupedRecords,
      header: header,
      recordCount: records.length,
      filteredRecordCount: filteredRecords.length
    };
  }

  async getUserInitials(record: AuditHistoryRelationshipModel) {
    if (record) {
      const firstName = record.FirstName;
      const lastName = record.LastName;
      const firstInitial = (firstName && firstName.length > 0) ? firstName.charAt(0) : "";
      const lastInitial = (lastName && lastName.length > 0) ? lastName.charAt(0) : "";
      return firstInitial.toUpperCase() + lastInitial.toUpperCase();
    } else {
      return "??";
    }
  }

  generateKnockoutReferenceFieldDefinition(field: AuditHistoryRelationshipModel, label: string, relatedTable: string, relatedID: string): KnockoutFieldModel {

    const answerValue = field?.displayValue ? relatedID : null;
    const answerDisplayValue = field?.displayValue ? field?.displayValue : null;

    return {
      name: relatedTable,
      i18nKey: label,
      datatype: 'reference',
      availableValues: [],
      referenceTable: relatedTable,
      readonly: true,
      mandatory: false,
      enablePreview: true,
      tooltip: null,
      data: {
        defaultValue: null,
        defaultValueSource: 'script',
        answer: {
          value: answerValue,
          displayValue: answerDisplayValue
        },
      },
      requestId: null,
      table: 'SyntheticTable',
      renderer: "onetoone",
      rendererProperties: {},
    };
  }

  generateKnockoutStringFieldDefinition(field: AuditHistoryFieldModel, label: string, renderer?: string): KnockoutFieldModel {
    if (!renderer) {
      renderer = "textfield";
    }

    const answerValue = field?.displayValue ? field?.value as string : null;
    const answerDisplayValue = field?.displayValue ? field?.displayValue : null;

    return {
      name: 'String',
      i18nKey: label,
      datatype: 'text',
      availableValues: [],
      referenceTable: null,
      readonly: true,
      mandatory: false,
      enablePreview: false,
      tooltip: null,
      data: {
        defaultValue: null,
        defaultValueSource: 'script',
        answer: {
          value: answerValue,
          displayValue: answerDisplayValue
        },
      },
      requestId: null,
      table: 'SyntheticTable',
      renderer: renderer,
      rendererProperties: {},
    };
  }

  /**
   * Sometimes the UI goes into a spin and requests the audit history in a busy loop. We use a backoff approach
   * so we don't blow up the server
   */
  requestAuditHistory(next: RequestAuditHistory) {
    this.featureFlagsService.isOn("audit-history-journal-debugging-display") && console.log("AuditHistory.requestAuditHistory", next);
    this.requestAuditHistoryBackoff.next(next);
  }

  requestAuditHistoryImmediate(next: RequestAuditHistory) {
    this.featureFlagsService.isOn("audit-history-journal-debugging-display") && console.log("AuditHistory.requestAuditHistoryImmediate", next);
    this.requestAuditHistoryInvoke(next);
  }

  private requestAuditHistoryInvoke(next: RequestAuditHistory) {
    this.featureFlagsService.isOn("audit-history-journal-debugging-display") && console.log("AuditHistory.requestAuditHistoryInvoke", next);
    const messageBus = (window as any).MessageBus;

    if (next.tableName == null) {
      console.error("requestAuditHistoryInvoke: tableName not specified");
    }

    messageBus.publish(messageBus.CHANNELS.FORM_API_EVENT,
      "requestAuditHistory",
      {
        tableName: next.tableName,
        id: next.id,
        fieldName: next.fieldName,
      });
  }

  private merge(displayValue: AuditHistoryFieldModel, value: AuditHistoryFieldModel): AuditHistoryFieldModel {
    return {
      displayValue: displayValue.displayValue || value.displayValue,
      value: displayValue.value || value.value
    };
  }

}
